// ============================================================================
// 정통 한국장기 규칙 엔진 (Korean Janggi Complete Rule Engine)
// - 9x10 격자, 궁성 대각선, 포(包) 다리/포끼리 불가, 마/상 멱(길목) 막힘
// - 4대 포진(마상마상, 상마상마, 마상상마, 상마마상)
// - 장군(Check), 멍군(Defense), 외통수(Checkmate), 빅장(Bikjang), 3회 반복 금수
// - 대한장기협회 기물 점수제 (차13, 포7, 마5, 상3, 사3, 졸/병2 + 한 1.5점 덤)
// ============================================================================

export type Side = 'cho' | 'han';
export type PieceType = 'king' | 'cha' | 'po' | 'ma' | 'sang' | 'sa' | 'jol';
export type FormationType = 'maSangMaSang' | 'sangMaSangMa' | 'maSangSangMa' | 'sangMaMaSang';

export interface Piece {
  id: string;
  side: Side;
  type: PieceType;
}

export interface Pos {
  row: number; // 0 (top, Han camp) .. 9 (bottom, Cho camp)
  col: number; // 0 (left) .. 8 (right)
}

export interface Move {
  from: Pos;
  to: Pos;
  piece: Piece;
  captured?: Piece | null;
  isPass?: boolean;
  gaveCheck?: boolean;
  wasMeonggun?: boolean;
  notation?: string;
}

export type BoardGrid = (Piece | null)[][];

export const PIECE_SCORES: Record<PieceType, number> = {
  king: 0,
  cha: 13,
  po: 7,
  ma: 5,
  sang: 3,
  sa: 3,
  jol: 2
};

export const PIECE_NAMES_KO: Record<PieceType, { cho: string; han: string; hanjaCho: string; hanjaHan: string }> = {
  king: { cho: '초궁', han: '한궁', hanjaCho: '楚', hanjaHan: '漢' },
  cha: { cho: '차', han: '차', hanjaCho: '車', hanjaHan: '車' },
  po: { cho: '포', han: '포', hanjaCho: '包', hanjaHan: '包' },
  ma: { cho: '마', han: '마', hanjaCho: '馬', hanjaHan: '馬' },
  sang: { cho: '상', han: '상', hanjaCho: '象', hanjaHan: '象' },
  sa: { cho: '사', han: '사', hanjaCho: '士', hanjaHan: '士' },
  jol: { cho: '졸', han: '병', hanjaCho: '卒', hanjaHan: '兵' }
};

export const FORMATION_LABELS: Record<FormationType, { name: string; desc: string }> = {
  maSangMaSang: { name: '마상마상 (정형)', desc: '공수 균형이 가장 뛰어난 표준 포진' },
  sangMaSangMa: { name: '상마상마 (양귀마)', desc: '양쪽 귀마로 중앙과 측면을 단단히 지키는 포진' },
  maSangSangMa: { name: '마상상마 (우귀마)', desc: '오른쪽 귀마와 중앙 상으로 빠른 공격을 노리는 포진' },
  sangMaMaSang: { name: '상마마상 (좌귀마)', desc: '왼쪽 귀마와 우측 기동력으로 변칙 공격을 펼치는 포진' }
};

export function createEmptyBoard(): BoardGrid {
  return Array.from({ length: 10 }, () => Array<Piece | null>(9).fill(null));
}

export function cloneBoard(board: BoardGrid): BoardGrid {
  return board.map(row => row.map(cell => (cell ? { ...cell } : null)));
}

function getFormationOrder(formation: FormationType): [PieceType, PieceType, PieceType, PieceType] {
  switch (formation) {
    case 'maSangMaSang':
      return ['ma', 'sang', 'ma', 'sang'];
    case 'sangMaSangMa':
      return ['sang', 'ma', 'sang', 'ma'];
    case 'maSangSangMa':
      return ['ma', 'sang', 'sang', 'ma'];
    case 'sangMaMaSang':
      return ['sang', 'ma', 'ma', 'sang'];
  }
}

export function createInitialBoard(
  choFormation: FormationType = 'sangMaSangMa',
  hanFormation: FormationType = 'sangMaSangMa'
): BoardGrid {
  const board = createEmptyBoard();

  // Top camp: Han (rows 0..3)
  const hanOrder = getFormationOrder(hanFormation);
  board[0][0] = { id: 'han_cha_l', side: 'han', type: 'cha' };
  board[0][1] = { id: 'han_inner_1', side: 'han', type: hanOrder[0] };
  board[0][2] = { id: 'han_inner_2', side: 'han', type: hanOrder[1] };
  board[0][3] = { id: 'han_sa_l', side: 'han', type: 'sa' };
  board[0][5] = { id: 'han_sa_r', side: 'han', type: 'sa' };
  board[0][6] = { id: 'han_inner_3', side: 'han', type: hanOrder[2] };
  board[0][7] = { id: 'han_inner_4', side: 'han', type: hanOrder[3] };
  board[0][8] = { id: 'han_cha_r', side: 'han', type: 'cha' };
  board[1][4] = { id: 'han_king', side: 'han', type: 'king' };
  board[2][1] = { id: 'han_po_l', side: 'han', type: 'po' };
  board[2][7] = { id: 'han_po_r', side: 'han', type: 'po' };
  for (let c = 0; c <= 8; c += 2) {
    board[3][c] = { id: `han_jol_${c}`, side: 'han', type: 'jol' };
  }

  // Bottom camp: Cho (rows 6..9)
  const choOrder = getFormationOrder(choFormation);
  for (let c = 0; c <= 8; c += 2) {
    board[6][c] = { id: `cho_jol_${c}`, side: 'cho', type: 'jol' };
  }
  board[7][1] = { id: 'cho_po_l', side: 'cho', type: 'po' };
  board[7][7] = { id: 'cho_po_r', side: 'cho', type: 'po' };
  board[8][4] = { id: 'cho_king', side: 'cho', type: 'king' };
  board[9][0] = { id: 'cho_cha_l', side: 'cho', type: 'cha' };
  board[9][1] = { id: 'cho_inner_1', side: 'cho', type: choOrder[0] };
  board[9][2] = { id: 'cho_inner_2', side: 'cho', type: choOrder[1] };
  board[9][3] = { id: 'cho_sa_l', side: 'cho', type: 'sa' };
  board[9][5] = { id: 'cho_sa_r', side: 'cho', type: 'sa' };
  board[9][6] = { id: 'cho_inner_3', side: 'cho', type: choOrder[2] };
  board[9][7] = { id: 'cho_inner_4', side: 'cho', type: choOrder[3] };
  board[9][8] = { id: 'cho_cha_r', side: 'cho', type: 'cha' };

  return board;
}

export function inBounds(row: number, col: number): boolean {
  return row >= 0 && row < 10 && col >= 0 && col < 9;
}

export function isInPalace(row: number, col: number, camp?: Side): boolean {
  if (col < 3 || col > 5) return false;
  if (camp === 'han') return row >= 0 && row <= 2;
  if (camp === 'cho') return row >= 7 && row <= 9;
  return (row >= 0 && row <= 2) || (row >= 7 && row <= 9);
}

// Palace diagonal adjacency map: only corners (3,r0),(5,r0),(3,r2),(5,r2) connect through center (4,r1)
export function getPalaceDiagonalTargets(row: number, col: number): Pos[] {
  const targets: Pos[] = [];
  for (const rBase of [0, 7]) {
    const rCenter = rBase + 1;
    if (row === rCenter && col === 4) {
      targets.push(
        { row: rBase, col: 3 },
        { row: rBase, col: 5 },
        { row: rBase + 2, col: 3 },
        { row: rBase + 2, col: 5 }
      );
    } else if ((row === rBase || row === rBase + 2) && (col === 3 || col === 5)) {
      targets.push({ row: rCenter, col: 4 });
    }
  }
  return targets;
}

// Generate pseudo-legal target positions for a specific piece at (row, col)
export function getPseudoLegalMovesForPiece(board: BoardGrid, pos: Pos, overridePiece?: Piece): Pos[] {
  const { row, col } = pos;
  const piece = overridePiece || board[row]?.[col];
  if (!piece) return [];

  const side = piece.side;
  const moves: Pos[] = [];

  const canLand = (r: number, c: number): boolean => {
    if (!inBounds(r, c)) return false;
    const occ = board[r][c];
    return !occ || occ.side !== side;
  };

  switch (piece.type) {
    case 'king':
    case 'sa': {
      const camp: Side = row <= 2 ? 'han' : 'cho';
      const ortho = [
        { row: row - 1, col },
        { row: row + 1, col },
        { row, col: col - 1 },
        { row, col: col + 1 }
      ];
      for (const p of ortho) {
        if (isInPalace(p.row, p.col, camp) && canLand(p.row, p.col)) {
          moves.push(p);
        }
      }
      for (const p of getPalaceDiagonalTargets(row, col)) {
        if (isInPalace(p.row, p.col, camp) && canLand(p.row, p.col)) {
          moves.push(p);
        }
      }
      break;
    }

    case 'jol': {
      const forwardDir = side === 'cho' ? -1 : 1;
      const candidates: Pos[] = [
        { row: row + forwardDir, col },
        { row, col: col - 1 },
        { row, col: col + 1 }
      ];
      // Palace forward diagonals
      for (const d of getPalaceDiagonalTargets(row, col)) {
        if (d.row - row === forwardDir) {
          candidates.push(d);
        }
      }
      for (const p of candidates) {
        if (canLand(p.row, p.col)) moves.push(p);
      }
      break;
    }

    case 'ma': {
      // 1 step orthogonal (leg/마멱 must be empty) + 1 step outward diagonal
      const maSteps = [
        { leg: [-1, 0], dest: [-2, -1] },
        { leg: [-1, 0], dest: [-2, 1] },
        { leg: [1, 0], dest: [2, -1] },
        { leg: [1, 0], dest: [2, 1] },
        { leg: [0, -1], dest: [-1, -2] },
        { leg: [0, -1], dest: [1, -2] },
        { leg: [0, 1], dest: [-1, 2] },
        { leg: [0, 1], dest: [1, 2] }
      ];
      for (const s of maSteps) {
        const legR = row + s.leg[0];
        const legC = col + s.leg[1];
        const dstR = row + s.dest[0];
        const dstC = col + s.dest[1];
        if (!inBounds(legR, legC) || !inBounds(dstR, dstC)) continue;
        if (board[legR][legC] !== null) continue; // 마멱 막힘
        if (canLand(dstR, dstC)) {
          moves.push({ row: dstR, col: dstC });
        }
      }
      break;
    }

    case 'sang': {
      // 1 step orthogonal (leg1 must be empty) + 1 step diagonal (leg2 must be empty) + 1 step diagonal to dest
      const sangSteps = [
        { leg1: [-1, 0], leg2: [-2, -1], dest: [-3, -2] },
        { leg1: [-1, 0], leg2: [-2, 1], dest: [-3, 2] },
        { leg1: [1, 0], leg2: [2, -1], dest: [3, -2] },
        { leg1: [1, 0], leg2: [2, 1], dest: [3, 2] },
        { leg1: [0, -1], leg2: [-1, -2], dest: [-2, -3] },
        { leg1: [0, -1], leg2: [1, -2], dest: [2, -3] },
        { leg1: [0, 1], leg2: [-1, 2], dest: [-2, 3] },
        { leg1: [0, 1], leg2: [1, 2], dest: [2, 3] }
      ];
      for (const s of sangSteps) {
        const r1 = row + s.leg1[0], c1 = col + s.leg1[1];
        const r2 = row + s.leg2[0], c2 = col + s.leg2[1];
        const dstR = row + s.dest[0], dstC = col + s.dest[1];
        if (!inBounds(r1, c1) || !inBounds(r2, c2) || !inBounds(dstR, dstC)) continue;
        if (board[r1][c1] !== null || board[r2][c2] !== null) continue; // 상멱 2칸 중 하나라도 막히면 이동 불가
        if (canLand(dstR, dstC)) {
          moves.push({ row: dstR, col: dstC });
        }
      }
      break;
    }

    case 'cha': {
      // Orthogonal rays
      const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [dr, dc] of dirs) {
        let r = row + dr, c = col + dc;
        while (inBounds(r, c)) {
          const occ = board[r][c];
          if (!occ) {
            moves.push({ row: r, col: c });
          } else {
            if (occ.side !== side) moves.push({ row: r, col: c });
            break;
          }
          r += dr;
          c += dc;
        }
      }
      // Palace diagonal rays
      for (const rBase of [0, 7]) {
        const rCenter = rBase + 1;
        if (row === rCenter && col === 4) {
          for (const [cr, cc] of [[rBase, 3], [rBase, 5], [rBase + 2, 3], [rBase + 2, 5]]) {
            if (canLand(cr, cc)) moves.push({ row: cr, col: cc });
          }
        } else if ((row === rBase || row === rBase + 2) && (col === 3 || col === 5)) {
          const midOcc = board[rCenter][4];
          if (!midOcc) {
            moves.push({ row: rCenter, col: 4 });
            const oppR = row === rBase ? rBase + 2 : rBase;
            const oppC = col === 3 ? 5 : 3;
            if (canLand(oppR, oppC)) moves.push({ row: oppR, col: oppC });
          } else if (midOcc.side !== side) {
            moves.push({ row: rCenter, col: 4 });
          }
        }
      }
      break;
    }

    case 'po': {
      // Orthogonal cannon jump: must jump over exactly 1 non-po screen piece; cannot capture a po
      const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [dr, dc] of dirs) {
        let r = row + dr, c = col + dc;
        let screenFound = false;
        while (inBounds(r, c)) {
          const occ = board[r][c];
          if (!screenFound) {
            if (occ) {
              if (occ.type === 'po') break; // 포는 포를 넘을 수 없음
              screenFound = true;
            }
          } else {
            if (!occ) {
              moves.push({ row: r, col: c });
            } else {
              if (occ.side !== side && occ.type !== 'po') {
                moves.push({ row: r, col: c }); // 포는 포를 잡을 수 없음
              }
              break;
            }
          }
          r += dr;
          c += dc;
        }
      }
      // Palace diagonal cannon jump: from palace corner across center (4, rCenter) to opposite corner
      for (const rBase of [0, 7]) {
        const rCenter = rBase + 1;
        if ((row === rBase || row === rBase + 2) && (col === 3 || col === 5)) {
          const screen = board[rCenter][4];
          if (screen && screen.type !== 'po') {
            const oppR = row === rBase ? rBase + 2 : rBase;
            const oppC = col === 3 ? 5 : 3;
            const targetOcc = board[oppR][oppC];
            if (!targetOcc || (targetOcc.side !== side && targetOcc.type !== 'po')) {
              moves.push({ row: oppR, col: oppC });
            }
          }
        }
      }
      break;
    }
  }

  return moves;
}

export function findKing(board: BoardGrid, side: Side): Pos | null {
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      const p = board[r][c];
      if (p && p.side === side && p.type === 'king') {
        return { row: r, col: c };
      }
    }
  }
  return null;
}

export function isKingInCheck(board: BoardGrid, side: Side): boolean {
  const kingPos = findKing(board, side);
  if (!kingPos) return true;
  const enemySide: Side = side === 'cho' ? 'han' : 'cho';
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      const p = board[r][c];
      if (p && p.side === enemySide) {
        const targets = getPseudoLegalMovesForPiece(board, { row: r, col: c });
        if (targets.some(t => t.row === kingPos.row && t.col === kingPos.col)) {
          return true;
        }
      }
    }
  }
  return false;
}

// Check if both Kings face each other directly on the same column with no pieces in between (빅장 상태)
export function isBikjangState(board: BoardGrid): boolean {
  const choKing = findKing(board, 'cho');
  const hanKing = findKing(board, 'han');
  if (!choKing || !hanKing) return false;
  if (choKing.col !== hanKing.col) return false;
  const minR = Math.min(choKing.row, hanKing.row);
  const maxR = Math.max(choKing.row, hanKing.row);
  for (let r = minR + 1; r < maxR; r++) {
    if (board[r][choKing.col] !== null) return false;
  }
  return true;
}

export function applyMoveOnClone(board: BoardGrid, from: Pos, to: Pos): { nextBoard: BoardGrid; captured: Piece | null } {
  const nextBoard = cloneBoard(board);
  const moving = nextBoard[from.row][from.col];
  const captured = nextBoard[to.row][to.col];
  nextBoard[to.row][to.col] = moving;
  nextBoard[from.row][from.col] = null;
  return { nextBoard, captured };
}

export function getLegalMovesForPiece(board: BoardGrid, pos: Pos): Pos[] {
  const piece = board[pos.row]?.[pos.col];
  if (!piece) return [];
  const pseudo = getPseudoLegalMovesForPiece(board, pos);
  return pseudo.filter(to => {
    const { nextBoard } = applyMoveOnClone(board, pos, to);
    return !isKingInCheck(nextBoard, piece.side);
  });
}

export function getAllLegalMoves(board: BoardGrid, side: Side): Move[] {
  const allMoves: Move[] = [];
  const enemySide: Side = side === 'cho' ? 'han' : 'cho';
  const wasInCheck = isKingInCheck(board, side);

  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      const p = board[r][c];
      if (!p || p.side !== side) continue;
      const legalTargets = getLegalMovesForPiece(board, { row: r, col: c });
      for (const to of legalTargets) {
        const { nextBoard, captured } = applyMoveOnClone(board, { row: r, col: c }, to);
        const gaveCheck = isKingInCheck(nextBoard, enemySide);
        const move: Move = {
          from: { row: r, col: c },
          to,
          piece: p,
          captured,
          gaveCheck,
          wasMeonggun: wasInCheck && !isKingInCheck(nextBoard, side),
          notation: formatKoreanNotation(p, { row: r, col: c }, to, captured)
        };
        allMoves.push(move);
      }
    }
  }
  return allMoves;
}

export function formatKoreanNotation(piece: Piece, from: Pos, to: Pos, captured?: Piece | null): string {
  const sideLabel = piece.side === 'cho' ? '초' : '한';
  const pieceLabel = PIECE_NAMES_KO[piece.type][piece.side];
  const fromCoord = `${from.col + 1}·${from.row + 1}`;
  const toCoord = `${to.col + 1}·${to.row + 1}`;
  const capSuffix = captured ? ` (${PIECE_NAMES_KO[captured.type][captured.side]} 포획!)` : '';
  return `${sideLabel} ${pieceLabel} ${fromCoord} → ${toCoord}${capSuffix}`;
}

// Calculate official Korean Janggi Material Score (with 1.5 komi for Han)
export function calculateMaterialScores(board: BoardGrid): { cho: number; han: number; leader: Side; diff: number } {
  let cho = 0;
  let han = 1.5; // 후수(한) 덤 1.5점
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      const p = board[r][c];
      if (!p) continue;
      if (p.side === 'cho') cho += PIECE_SCORES[p.type];
      else han += PIECE_SCORES[p.type];
    }
  }
  return {
    cho,
    han,
    leader: cho > han ? 'cho' : 'han',
    diff: Math.abs(cho - han)
  };
}

export function getBoardHash(board: BoardGrid, turn: Side): string {
  let s = turn + ':';
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      const p = board[r][c];
      s += p ? `${p.side[0]}${p.type[0]}${p.type[1]}` : '.';
    }
  }
  return s;
}
